import { useAuthStore } from '../stores/authStore';

interface ApiError {
  error: string;
  message: string;
}

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public errorCode: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

// In dev, Vite proxies /v1/* to localhost:3001. In production, this points to API Gateway.
const API_BASE = import.meta.env.VITE_API_URL ?? '';
const isAuthBypass = import.meta.env.VITE_AUTH_BYPASS === 'true';

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const { userId, tier, idToken } = useAuthStore.getState();

  if (isAuthBypass) {
    // Dev bypass — send custom headers for backend dev middleware
    if (userId) {
      headers['x-user-id'] = userId;
      headers['x-user-tier'] = tier;
    }
  } else if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  return headers;
}

async function parseErrorResponse(res: Response): Promise<never> {
  let err: ApiError;
  try {
    err = await res.json();
  } catch {
    throw new ApiRequestError(res.status, 'SERVER_ERROR', `Request failed (${res.status})`);
  }
  throw new ApiRequestError(res.status, err.error, err.message);
}

function extractData<T>(json: unknown): T {
  if (json != null && typeof json === 'object' && 'data' in json) {
    return (json as { data: T }).data;
  }
  throw new ApiRequestError(500, 'INVALID_RESPONSE', 'Response missing data field');
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: buildHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });

  // On 401, attempt a token refresh and retry once
  if (res.status === 401 && !isAuthBypass) {
    const newToken = await useAuthStore.getState().refreshTokens();
    if (newToken) {
      const retryRes = await fetch(`${API_BASE}${path}`, {
        method,
        headers: buildHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!retryRes.ok) {
        await parseErrorResponse(retryRes);
      }

      return extractData<T>(await retryRes.json());
    }
    // refreshTokens() returned null → already logged out
    throw new ApiRequestError(401, 'UNAUTHORIZED', 'Session expired');
  }

  if (!res.ok) {
    await parseErrorResponse(res);
  }

  return extractData<T>(await res.json());
}

/** Retry on server errors (5xx) but not on client errors (4xx). */
export function retryOnServerError(_count: number, error: Error): boolean {
  if (error instanceof ApiRequestError) return error.status >= 500;
  return false;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
